import { Grid, makeStyles, createStyles, Theme } from "@material-ui/core";
import React, { useContext } from "react";
import UserReferenceDefault from "../../api/models/advanced-search/UserReferenceDefault";
import LaboratoryResponse from "../../api/models/LaboratoryResponse";
import AdvancedSearchApi from "../../api/services/AdvancedSearchApi";
import { LabAndUsersSearchAutocomplete } from "../../components/Autocomplete/CustomSearchAutoComplete.instances";
import CustomButton from "../../components/Button/CustomButton";
import InfoBox from "../../components/InfoBox";
import { DashboardContext } from "../../contexts/DashboardContext";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import { UserContext } from "../../contexts/UserContext";
import DashboardActionMenu from "./DashboardActionMenu";
import WaitingForCustomerFilter from "./WaitingForCustomerFilter/WaitingForCustomerFilter";
import { DashboardActionHandler } from "./DashboardAction";
import { RequestConfig } from "./RequestConfig";
import TestRequestCreationDialog from "./TestRequestCreation/TestRequestCreationDialog";

const CREATE_REQUEST = "Create Request";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    autocompleteGridItem: {
      [theme.breakpoints.down("lg")]: {
        paddingLeft: "0",
      },
      [theme.breakpoints.up("lg")]: {
        paddingLeft: "1%",
      },
    },
    createRequestButton: {
      marginRight: "10px",
    },
    gridFiller: {
      [theme.breakpoints.down("lg")]: {
        display: "block",
      },
      [theme.breakpoints.up("lg")]: {
        display: "none",
      },
    },
  }),
);

interface RequestControlsProps {
  isLoading: boolean;
  advancedSearchApi: AdvancedSearchApi;
  createNewRequest: (requestConfig?: RequestConfig) => void;
  waitingForCustomer: TestRequestRaw.Schemas.WaitingFilter;
  handleActionsMenuClick: DashboardActionHandler;
  handleWaitingForCustomerChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
}

const RequestControls = (props: RequestControlsProps) => {
  const {
    isLoading,
    advancedSearchApi,
    createNewRequest,
    waitingForCustomer,
    handleActionsMenuClick,
    handleWaitingForCustomerChange,
  } = props;

  const { user } = useContext(UserContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { updateLabOrUser, labOrUser } = useContext(DashboardContext);
  const { createTestRequest, listOtherTestRequests } = useContext(GenericPermissionContext);

  const [testRequestCreationDialogOpen, setTestRequestCreationDialogOpen] = React.useState(false);

  const classes = useStyles();

  const handleDialogSubmit = async (
    initialRequestConfig?: { aproe?: boolean; historical?: boolean },
    crmQueryParams?: { opportunityId?: string; requestType?: string },
  ) => {
    let finalQueryParams = crmQueryParams;

    if (crmQueryParams) {
      finalQueryParams = { ...crmQueryParams, opportunityId: crmQueryParams.opportunityId };
    }

    const completedRequestConfig = { ...initialRequestConfig, ...finalQueryParams };

    createNewRequest(completedRequestConfig);

    setTestRequestCreationDialogOpen(false);
  };

  return (
    <Grid container>
      <Grid item>
        <TestRequestCreationDialog
          open={testRequestCreationDialogOpen}
          onClose={() => setTestRequestCreationDialogOpen(false)}
          handleTestRequestCreation={handleDialogSubmit}
          header={CREATE_REQUEST.toUpperCase()}
        />
        {createTestRequest && (
          <InfoBox text="This is the starting point to create a new test request." flexChildren>
            <CustomButton
              text={CREATE_REQUEST}
              className={classes.createRequestButton}
              primary
              add
              onClick={() => setTestRequestCreationDialogOpen(true)}
            />
            <DashboardActionMenu onMenuClick={handleActionsMenuClick} loading={isLoading} />
          </InfoBox>
        )}
      </Grid>
      <Grid item style={{ flexGrow: 1 }} />
      <Grid item style={{ display: "flex", alignItems: "center" }}>
        <WaitingForCustomerFilter onChange={handleWaitingForCustomerChange} value={waitingForCustomer} />
      </Grid>
      {listOtherTestRequests && (
        <>
          <Grid item xs={6} className={classes.gridFiller} />
          <Grid item xs={6} lg={3} className={classes.autocompleteGridItem}>
            <LabAndUsersSearchAutocomplete
              inputLabel="Lab / User"
              map={(valueObj): { key: string; label: string } => {
                const isUserReference = valueObj !== null && Object.keys(valueObj).includes("userName");
                const label = isUserReference
                  ? (valueObj as UserReferenceDefault).userName
                  : (valueObj as LaboratoryResponse).name;

                return {
                  key: `${label}`,
                  label: `${label}`,
                };
              }}
              onChange={(e, val) => {
                if (typeof val === "object" && val !== null) {
                  const isUserReference = Object.keys(val).includes("userName");
                  if (isUserReference) {
                    updateLabOrUser((val as UserReferenceDefault) || null);
                  } else {
                    updateLabOrUser(val as LaboratoryResponse);
                  }
                } else {
                  updateLabOrUser(null);
                }
              }}
              optionFetch={(query: string) => {
                return advancedSearchApi
                  .searchLabsAndUsers(query)
                  .then((res) => {
                    const userOption: UserReferenceDefault = {
                      userEmail: user?.userEmail,
                      userId: user?.userId,
                      userName: `${user?.userName} (show my requests only)`,
                    };
                    const options = [userOption, ...res.data.laboratories, ...res.data.users];

                    return { data: options, totalPages: 1 };
                  })
                  .catch((error) => {
                    handleHttpError(error);
                    return { data: [], totalPages: 0 };
                  });
              }}
              value={labOrUser}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default RequestControls;
